import { WebChatConfigSchema } from "@chatbotgang/web-sdk-core/model";
import { safeEnumSchema } from "@polifonia/utils/zod/safeEnumSchema";
import { z } from "zod";

import {
  BillingFeatureSchema,
  BillingProductSchema,
  CreditSourceTypeSchema,
  PaymentMethodSchema,
  TopUpStatusSchema,
} from "./billingConstants";

export const ErrorSchema = z.object({
  message: z.string(),
  code: z.enum([
    "UNAUTHORIZED",
    "CHANNEL_WCCS_INFORMATION_URL_ALREADY_EXISTS",
    "ORGANIZATION_NOT_FOUND",
    "BALANCE_INSUFFICIENT",
    "SUBSCRIPTION_NOT_FOUND",
    "MONTHLY_USAGE_REPORT_NOT_FOUND",
    "UNIFY_SCOPE_SETTING_NOT_FOUND",
    "INTEGRATE_CDH_FAILED",
    "UNIFY_KEY_UPDATE_FAIL",
    "DISCONNECT_CDH_FAILED",
    "GET_MERGE_STATE_FAILED",
    "SERVE_CONTACT_UPDATE_EVENT_FAILED",
    "GET_ORGANIZATIONS_FAILED",
    "CHANNEL_NOT_FOUND",
    "CHANNEL_WCCS_INFORMATION_NOT_FOUND",
    "ORGANIZATION_SETTING_NOT_FOUND",
    /**
     * WebStream subdomain errors
     */
    "CHANNEL_SUBDOMAIN_INVALID",
    "CHANNEL_SUBDOMAIN_ALREADY_EXISTS",
    /**
     * channel creation/connection errors
     */
    "CHANNEL_ALREADY_EXISTS",
    "IG_BUSINESS_ACCOUNT_NOT_FOUND",
    "UNKNOWN_ERROR",
    /**
     * Google Ads OAuth connection errors, forwarded by interlude with
     * google-ads-integration's own error names
     */
    "InvalidOAuthState",
    "OAuthStateExpired",
    "OAuthCodeExchangeFailed",
    "OAuthScopeNotGranted",
    "RefreshTokenNotIssued",
    "GoogleOAuthUnavailable",
    "OAuthCredentialNotFound",
    "ManagerAccountAlreadyRegistered",
    "ManagerAccountNotFound",
    "CustomerNotAccessible",
    "ManagerAccountNotOAuth",
    "OAuthRefreshTokenInvalid",
    "AuthenticationError",
    /**
     * Google Ads Manager Account errors
     */
    "GOOGLE_ADS_CANNOT_DELETE_MANAGER_ACCOUNT",
    /**
     * Meta Ads errors
     */
    "META_ADS_CONNECT_FAILED",
    /**
     * email channel validation errors
     */
    "SENDER_DOMAIN_NOT_FOUND",
    "SENDER_PROFILE_NOT_FOUND",
    "SENDER_PROFILE_ALREADY_EXISTS",
    "SENDER_PROFILE_DOMAIN_MISMATCH",
    "INVALID_REQUEST",
    "EXTERNAL_SERVICE_ERROR",
    /**
     * Custom Field errors
     */
    "CUSTOM_FIELD_NOT_FOUND",
    "CUSTOM_FIELD_KEY_DUPLICATE",
    "CUSTOM_FIELD_NAME_DUPLICATE",
    "CUSTOM_FIELD_ACTIVE_LIMIT_EXCEEDED",
    "CUSTOM_FIELD_SLOT_LIMIT_EXCEEDED",
    /**
     * Raised by the field-level PATCH endpoint when a request carries none of
     * `name`, `description` or `options`. Not single-select-specific -- it is
     * about the patch being empty, and any future field type reaching that
     * endpoint can trigger it -- so it sits with the other field-level codes
     * rather than in the group below.
     */
    "CUSTOM_FIELD_PATCH_EMPTY",
    /**
     * Single select custom field errors.
     *
     * Nothing sends or receives these yet: the endpoint that raises them is
     * still being built. They are listed here first so that a response
     * carrying one is recognized as a known error rather than reported to
     * Sentry as an unknown one.
     */
    "CUSTOM_FIELD_SINGLE_SELECT_NOT_ENABLED",
    "CUSTOM_FIELD_SINGLE_SELECT_FIELD_TYPE_INVALID",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_STALE",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_DUPLICATE",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_LIMIT_EXCEEDED",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_NAME_INVALID",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_DESCRIPTION_INVALID",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_ACTIVE_INVALID",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_REQUIRED_FIELD_MISSING",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_READONLY_FIELD",
    "CUSTOM_FIELD_SINGLE_SELECT_OPTION_UNKNOWN_FIELD",
    /**
     * Billing errors
     */
    "BILLING_NOT_CONFIGURED",
    "ACCESS_TOKEN_ISSUE_FAILED",
    /**
     * Email Onboard errors
     */
    "EMAIL_ONBOARD_ORG_ALREADY_HAS_CHANNEL",
    "EMAIL_ONBOARD_BRAND_DOMAIN_TAKEN",
    "EMAIL_ONBOARD_APPLICATION_NOT_FOUND",
    /**
     * Meta Ads errors
     */
    "META_ADS_INVALID_TOKEN",
    "META_ADS_ALREADY_CONNECTED",
    "META_ADS_NOT_CONNECTED",
    /**
     * LINE Ads errors
     */
    "LINE_ADS_INVALID_CREDENTIALS",
    "LINE_ADS_DATA_PROVIDER_PERMISSION_REQUIRED",
    "LINE_ADS_UNAVAILABLE",
    /**
     * Contract signing errors
     */
    "SIGNING_NOT_ALLOWED",
    "INVALID_AGREEMENT_VERSIONS",
    "CONTRACT_SIGNING_NOT_AVAILABLE",
    /**
     * OpenAPI v2 self-service API-key errors.
     *
     * Interlude has been returning these since the api-key endpoints shipped;
     * they were missing here, so `ErrorSchema.safeParse` rejected the body and
     * every one of them reached `logError` instead of a message. Listing them
     * is what lets the global handler speak for them.
     *
     * Interlude defines a fifth, `OPENAPI_ORG_NOT_IN_AUTH_CONTEXT`, which is
     * not listed: it is raised with a 401, and a 401 already goes down the
     * token-expiry path in `interludeAxios`, so it is left alone here.
     */
    "OPENAPI_V2_NOT_ENABLED",
    "OPENAPI_ORG_NOT_FOUND",
    "OPENAPI_CREDENTIAL_NOT_FOUND",
    "OPENAPI_KONG_UNAVAILABLE",
  ]),
});

export const UnificationKeysSchema = z.enum([
  "display_email",
  "custom_id",
  "connect_id",
  "display_mobile",
  "line_id",
  "fb_id",
  "ig_id",
  "whatsapp_bsuid",
]);

export const OrgSourceSchema = z.enum(["maac", "caac"]);

export const OrgSchema = z.object({
  id: z.number().int(),
  source: OrgSourceSchema,
  name: z.string(),
  uuid: z.string(),
});

export const GoogleAdsAuthTypeSchema = z.enum(["service_account", "oauth"]);

export const GoogleAdsManagerAccountSchema = z.object({
  id: z.number().int().nullable(),
  orgUuid: z.string().uuid(),
  googleAdsCustomerId: z.string(),
  name: z.string(),
  enableSync: z.boolean(),
  isManager: z.boolean().default(true),
  authType: GoogleAdsAuthTypeSchema,
  hasAccess: z.boolean().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});

export const ManagerAccountRegistrationRequestSchema = z.object({
  customerId: z.string(),
  oauthCredentialId: z.number().int(),
});

export const ManagerAccountReauthorizeRequestSchema = z.object({
  oauthCredentialId: z.number().int(),
});

export const GoogleAdsOAuthAuthorizationUrlResponseSchema = z.object({
  authorizationUrl: z.string().url(),
});

export const GoogleAdsOAuthCallbackRequestSchema = z.object({
  code: z.string(),
  state: z.string(),
});

export const GoogleAdsAccessibleCustomerSchema = z.object({
  customerId: z.string(),
  name: z.string(),
  isManager: z.boolean(),
});

export const GoogleAdsOAuthCallbackResponseSchema = z.object({
  oauthCredentialId: z.number().int(),
  customers: z.array(GoogleAdsAccessibleCustomerSchema),
});

export const ManagerAccountUpdateRequestSchema = z.object({
  enableSync: z.boolean(),
});

export const ConversionSettingResponseSchema = z.object({
  id: z.number().int().nullable(),
  googleAdsAccountId: z.number().int(),
  conversionActionId: z.string(),
  conversionActionType: z.string(),
  conversionActionName: z.string(),
  conversionActionCategory: z.string(),
  hasAccess: z.boolean().nullable(),
  lastQueryTimestamp: z.string().datetime().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export const ManagerAccountSchema = z.object({
  name: z.string(),
  customerId: z.string(),
});

export const GoogleAdsAccountWithConversionSettingsResponseSchema = z.object({
  id: z.number().int().nullable(),
  googleAdsManagerAccountId: z.number().int(),
  googleAdsCustomerId: z.string(),
  name: z.string(),
  hasAccess: z.boolean().nullable(),
  conversionSettings: z.array(ConversionSettingResponseSchema).nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});

export const GoogleAdsAccountListResponseSchema = z.object({
  accounts: z.array(GoogleAdsAccountWithConversionSettingsResponseSchema),
  total: z.number().int(),
});

export const CreateConversionSettingRequestSchema = z.object({
  conversionActionId: z.string(),
  conversionActionType: z.string(),
  conversionActionName: z.string(),
  conversionActionCategory: z.string(),
});

export const UpdateConversionSettingsRequestSchema = z.object({
  conversionActionIds: z.array(z.string()),
});

export const UpdateConversionSettingsResponseSchema = z.object({
  settings: z.array(ConversionSettingResponseSchema),
  total: z.number().int(),
});

/**
 * ConversionMapping: per-ad-account mapping from a user-facing event in
 * admin center to a Google Ads conversion action with value/currency.
 */
export const ConversionStatusSchema = z.enum(["active", "disabled"]);

export const ConversionMappingResponseSchema = z.object({
  id: z.number().int(),
  googleAdsAccountId: z.number().int(),
  status: ConversionStatusSchema,
  conversionActionId: z.string(),
  conversionValue: z.string().nullable(),
  currencyCode: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export const ConversionMappingListResponseSchema = z.object({
  conversionMappings: z.array(ConversionMappingResponseSchema),
  total: z.number().int(),
});

export const CreateConversionMappingRequestSchema = z.object({
  status: ConversionStatusSchema.optional(),
  conversionActionId: z.string(),
  conversionValue: z.string().nullable().optional(),
  currencyCode: z.string().nullable().optional(),
});

export const UpdateConversionMappingRequestSchema = z.object({
  status: ConversionStatusSchema.optional(),
  conversionActionId: z.string().optional(),
  conversionValue: z.string().nullable().optional(),
  currencyCode: z.string().nullable().optional(),
});

/**
 * Accounts → conversion actions, for the Add Conversion Mapping modal picker.
 */
export const ConversionActionsItemSchema = z.object({
  conversionActionId: z.string(),
  name: z.string(),
  defaultCurrencyCode: z.string().nullable(),
});

export const ConversionActionsAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  conversionActions: z.array(ConversionActionsItemSchema),
});

export const ConversionActionsResponseSchema = z.object({
  accounts: z.array(ConversionActionsAccountSchema),
});

/**
 * Readiness of a Google Ads account for identifier-only (Enhanced Conversions for
 * Leads) uploads. Both flags must be true or Google rejects the upload; the Add
 * Conversion Mapping modal warns the operator which one is still off.
 */
export const LeadConversionReadinessResponseSchema = z.object({
  acceptedCustomerDataTerms: z.boolean(),
  enhancedConversionsForLeadsEnabled: z.boolean(),
});

export const UnifyScopeSettingSchema = z.object({
  unifyScopeId: z.number().int(),
  isCdhConnected: z.boolean(),
  orgs: OrgSchema.array(),
  unifyKeys: z
    .object({
      attribute: UnificationKeysSchema,
      isDistinct: z.boolean(),
    })
    .array(),
});

export const ChannelSchema = z.object({
  id: z.number().int().positive(),
  organizationId: z.number().int().positive(),
  uuid: z.string(),
  name: z.string(),
  type: z.enum(["wccs", "whatsapp", "line", "fb", "ig", "email"]),
  status: z.enum(["connected", "disconnected", "token-expired"]),
  externalChannelId: z.string().nullable(),
});

export const WccsChannelSchema = ChannelSchema.extend({
  type: z.literal("wccs"),
  channelInformation: z.object({
    webChatModuleEnabled: z.boolean(),
    webTrackingEnabled: z.boolean(),
    smartRedirectToolEnabled: z.boolean(),
    domains: z
      .object({
        id: z.number().int().positive(),
        url: z.string(),
      })
      .array(),
    webChatConfig: WebChatConfigSchema.nullable(),
    /**
     * WebStream flavor discriminator. `undefined` (existing channels) and
     * "web-chat" both mean "regular WCCS/WebChat channel" — only
     * "web-streams" routes the channel to the WebStream settings page
     * instead of the normal WebChatConfig one. Set on the interlude side
     * outside this task's scope.
     */
    type: z.enum(["web-streams", "web-chat"]).optional(),
    /**
     * Web-streams-only. Globally unique across all channels; interlude
     * always sends the full current value, defaulting to "" when unset —
     * never `null` (see interlude PR #1216, confirmed deployed to staging
     * and both production regions). `.optional()` anyway, matching the
     * sibling `type` field above — this parses every wccs channel (web-chat
     * included, ungated), so staying tolerant of a field an older deployed
     * version might omit costs nothing and avoids a repeat of this kind of
     * rollout-timing risk.
     */
    subdomain: z.string().optional(),
    /**
     * Internal, temporary per-channel exceptions owned by interlude. Closer
     * to a feature flag than to a setting: it is switched on by hand inside
     * the company, it is not part of the customer's configuration, and Admin
     * Center never writes it.
     *
     * Nothing in Admin Center reads it today: the parse keeps an empty object
     * and drops whatever interlude stores. The field stays declared so that a
     * future experiment the settings preview has to mirror adds its key here,
     * behind the same `.catch()`, instead of reintroducing the field.
     *
     * It is not writable from here. Interlude's PATCH body is a separate,
     * explicitly declared field list, and `UpdateWccsChannelRequestSchema` in
     * `api/channel.ts` mirrors that by picking the writable fields one by one
     * rather than reusing this object, so adding a field here does not make
     * it settable.
     *
     * Nothing validates the value between the database column and this parse,
     * so `.catch()` reads a broken or missing value as `undefined` instead of
     * failing the channel response that feeds the whole settings page.
     */
    experimentalConfig: z.object({}).optional().catch(undefined),
    /**
     * Whether the chat window hides its "Powered by Crescendo Lab" footer.
     * A paid capability: interlude sends the effective value, which is true
     * only when the organization has `wccsCustomChatFooterEnabled` and the
     * customer chose to hide the footer on this channel. An organization
     * without the capability therefore reads `false` here even if it stored
     * `true`.
     *
     * The customer writes the choice through `updateChannel` (see
     * `UpdateWccsChannelRequestSchema` in `api/channel.ts`).
     *
     * Only an explicit `true` hides the footer: a missing or malformed value
     * costs one line of footer, not a failed channel parse that takes the
     * whole settings page with it.
     *
     * Backend: chatbotgang/interlude#1405.
     */
    webChatFooterDisabled: z.boolean().catch(false),
  }),
});

const webStreamLocaleMessages = <Schema extends z.ZodTypeAny>(schema: Schema) =>
  z
    .object({
      enUs: schema,
      th: schema,
      ja: schema,
      zhTw: schema,
    })
    // Passthrough so locales beyond the 4 system languages survive the
    // read/edit/save round-trip instead of being silently stripped.
    .passthrough();

export const WebStreamMenuMessageSchema = z
  .object({
    title: z.string(),
    subtitle: z.string(),
    teamTabText: z.string(),
    memberTabText: z.string(),
    searchPlaceholder: z.string(),
    searchClear: z.string(),
    categoryAll: z.string(),
    consult: z.string(),
    historyEmpty: z.string(),
    findTeam: z.string(),
  })
  .passthrough();

export const WebStreamReentryMessageSchema = z
  .object({
    title: z.string(),
    description: z.string(),
    buttonText: z.string(),
  })
  .passthrough();

export const WebStreamColorsSchema = z.object({
  primaryColor: z.string(),
  secondaryBackgroundColor: z.string(),
  unreadColor: z.string(),
});

export const WebStreamBannerScaleSchema = z.enum(["small", "medium", "large"]);

/**
 * Channel-wide typography. `baseFontSize` is an integer px value driving
 * Vivace's `--cl-widget-base-font-size`, which scales every text size on the
 * WebStream proportionally. The settings-page slider offers 14-22 and Vivace's
 * renderer clamps to that same range; absent means 16, the size every existing
 * channel already renders at.
 *
 * Deliberately NOT range-checked here, mirroring Vivace's schema: a value
 * outside 14-22 is still a rendered font size (clamped), so rejecting it would
 * take the channel's colors, copy and hero image down with it over a field the
 * renderer can handle on its own. `.catch(undefined)` covers the genuinely
 * unusable case (a string, a negative) by reading it as "not set" rather than
 * failing the whole config.
 *
 * `baseFontSize` is optional inside an otherwise-present `typography` object on
 * purpose: a stored config written by another producer may carry only keys this
 * page does not manage, and a required inner field would turn that into a GET
 * parse failure that breaks the whole settings page.
 */
export const WebStreamTypographySchema = z
  .object({
    baseFontSize: z.number().int().positive().optional().catch(undefined),
  })
  .passthrough();

/**
 * Dialogue composer size: the message input's starting height as a percentage
 * of the screen height, and the attach/send button size in px. Mirrors
 * Vivace's `composer` (ranges in `COMPOSER_SIZE_RANGES`), with the same
 * contract as `baseFontSize`: plain positive integers, clamped by the renderer,
 * and absent means default-ui's composer as it renders today. Unlike
 * `baseFontSize`, the form does not fill a default here — writing one would
 * change how every channel that only saved another field renders.
 */
export const WebStreamComposerSchema = z
  .object({
    inputMinHeight: z.number().int().positive().optional().catch(undefined),
    buttonSize: z.number().int().positive().optional().catch(undefined),
  })
  .passthrough();

/**
 * Per-channel WebStream white-label config — the object interlude proxies
 * to/from `clients/{externalChannelId}/config.json` on GCS. Read side of
 * Vivace's `WebStreamConfigSchema` (`packages/core/src/model.ts`).
 *
 * Designed to tolerate an independently-evolving stored shape (see the read
 * path decision in `api/channel.ts`):
 * - `.passthrough()` at every level so fields this settings page does not
 *   manage (e.g. `reentry.keyword` / `lineOaId`, extra locales, future keys)
 *   survive the read/edit/save round-trip instead of being stripped.
 * - `menu.colors` is optional: it is a frontend-ahead field for the upcoming
 *   color-customization feature that neither Vivace's reader nor existing
 *   stored configs have yet. The form fills defaults when it is absent.
 * - `typography` and `menu.bannerScale` are optional for the same reason: every
 *   channel created before the text-size settings existed stores a config
 *   without them, and absent has to keep meaning "what it renders today"
 *   (16px base, medium banner) rather than an invalid config.
 */
export const WebStreamConfigSchema = z
  .object({
    version: z.literal(2),
    // Top-level rather than under `menu`: the menu, dialogue and expired screens
    // all read it, so scoping it to `menu` would repeat the mistake `menu.colors`
    // made (a channel-level value parked in a screen-level object).
    typography: WebStreamTypographySchema.optional(),
    // Top-level beside `typography` rather than inside it: neither value is a
    // type size. Only the dialogue screen reads it.
    composer: WebStreamComposerSchema.optional(),
    menu: z
      .object({
        heroImageUrl: z.string(),
        colors: WebStreamColorsSchema.optional(),
        /**
         * Banner (menu hero title/subtitle) size, deliberately NOT scaled by
         * `typography.baseFontSize`: three fixed steps of title/subtitle px,
         * 18/14, 22/16 and 24/18. Scoped to `menu` because the banner exists
         * only on the menu screen -- unlike `colors`, which is channel-level.
         */
        bannerScale: WebStreamBannerScaleSchema.optional().catch(undefined),
        messages: webStreamLocaleMessages(WebStreamMenuMessageSchema),
      })
      .passthrough(),
    reentry: z
      .object({
        messages: webStreamLocaleMessages(WebStreamReentryMessageSchema),
      })
      .passthrough(),
  })
  .passthrough();

export const WhatsappChannelSchema = ChannelSchema.extend({
  type: z.literal("whatsapp"),
  channelInformation: z.object({
    phoneNumber: z.string(),
    wabaInformation: z
      .object({
        name: z.string(),
        wabaId: z.string(),
      })
      // TODO: Remove this once the backend is updated
      .nullable(),
  }),
});

/**
 * Spec: interlude `plans/whatsapp-conversational-components-api-contract.md`
 * -- GET/PATCH `/api/v1/channels/{channel_id}/conversational-components`.
 *
 * Field names here are camelCase (`interludeAxios` camelcases every
 * response), matching the contract's `prompts` 1:1 -- the contract's other
 * two response fields (`commands`, `enable_welcome_message`) are
 * intentionally not modeled: Phase 1 UI only reads/writes `prompts`, and the
 * api-patterns skill asks schemas to include only what the frontend actually
 * consumes. Zod objects strip unknown keys by default, so this remains a
 * valid parse of the full response. Phase 2 (Commands tab) adds `commands`.
 */
export const WhatsappConversationalComponentsSchema = z.object({
  prompts: z.array(z.string()),
});

const FacebookChannelSchema = ChannelSchema.extend({
  type: z.literal("fb"),
  channelInformation: z.object({}),
});
const InstagramChannelSchema = ChannelSchema.extend({
  type: z.literal("ig"),
  channelInformation: z.object({}),
});

export const LineChannelSchema = ChannelSchema.extend({
  organizationId: z.number().int().positive().nullable(),
  type: z.literal("line"),
  channelInformation: z.object({}),
});

/**
 * Response of `POST /api/v1/channels/line` (interlude#1362). interlude does
 * not store the channel: it forwards the ID and secret to cantata, which
 * validates them and creates the channel.
 */
export const LineChannelCreateResponseSchema = z.object({
  uuid: z.string(),
  /** The OA's display name, read from LINE. */
  name: z.string(),
  /** The OA's basic ID, such as `@123abcde`. */
  basicId: z.string(),
  /**
   * Whether LINE's "Use webhook" switch is on. `null` when cantata could not
   * read it; callers must not read that as "off".
   */
  webhookActive: z.boolean().nullable(),
});

/**
 * Response of `POST /api/v1/channels/line/{uuid}/webhook-check`
 * (interlude#1362). cantata checks the webhook and tries to repair it, so
 * `active` is the state after that attempt. `null` means LINE could not be
 * read.
 */
export const LineWebhookCheckSchema = z.object({
  active: z.boolean().nullable(),
  /**
   * The webhook URL cantata registers with LINE. Required because
   * `LineChannelWebhookCheckResponse` in interlude#1362 `schemas.py` types it
   * as a plain `str`, so every 200 carries it.
   */
  endpoint: z.string(),
});

export const SenderDomainDnsRecordSchema = z.object({
  /** Canonical record identifier: "mail_cname" / "dkim1" / "dkim2" / "dmarc". */
  name: z.string(),
  /** DNS record type: "cname" / "txt". */
  type: z.string(),
  /** Host (the record's name in the customer's DNS zone). */
  host: z.string(),
  /** Value the customer needs to set. */
  data: z.string(),
  /** Last validation result for this record. */
  valid: z.boolean(),
});

export const SenderDomainSchema = z.object({
  id: z.number().int().positive(),
  domain: z.string(),
  status: z.enum(["verified", "warning", "failed"]),
  mailCnameStatus: z.enum(["pass", "fail"]),
  dkim1Status: z.enum(["pass", "fail"]),
  dkim2Status: z.enum(["pass", "fail"]),
  dmarcStatus: z.enum(["pass", "fail"]),
  dnsRecords: z.array(SenderDomainDnsRecordSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const SenderProfileSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  fromEmail: z.string(),
  replyTo: z.string(),
  status: z.enum(["verified", "warning", "failed"]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const EmailChannelSchema = ChannelSchema.omit({
  status: true,
}).extend({
  type: z.literal("email"),
  status: z.enum([
    "connected",
    "disconnected",
    "not_set",
    "healthy",
    "warning",
    "restricted",
    "suspended",
    "building_reputation",
  ]),
  channelInformation: z.object({
    // The POST /channels/ (createChannel) response returns an empty
    // `channel_information: {}` for a freshly created email channel, whereas
    // GET populates it. Default both arrays to [] so the create response still
    // validates — otherwise the missing keys fail validation and the user sees
    // an "unexpected error" before the onboarding flow can advance to DNS
    // verification (sender domains are fetched separately afterwards).
    senderDomains: z
      .array(SenderDomainSchema.pick({ id: true, domain: true, status: true }))
      .default([]),
    senderProfiles: z
      .array(
        SenderProfileSchema.pick({ id: true, fromEmail: true, status: true }),
      )
      .default([]),
  }),
});

export const EmailSendingUsageSchema = z.object({
  usage: z.number().int().nonnegative(),
  capacity: z.number().int().nonnegative(),
  updatedAt: z.string().datetime().nullable(),
});

export const EmailHealthMetricsSchema = z.object({
  sendingSpeed: z.number().nullable(),
  bounceCount: z.number().int().nonnegative(),
  spamCount: z.number().int().nonnegative(),
  totalSent: z.number().int().nonnegative(),
  updatedAt: z.string().datetime().nullable(),
});

export const ChannelWithInformationSchema = z.discriminatedUnion("type", [
  WccsChannelSchema,
  WhatsappChannelSchema,
  LineChannelSchema,
  FacebookChannelSchema,
  InstagramChannelSchema,
  EmailChannelSchema,
]);

export const MaacRoleNameSchema = z
  .custom<`maac:${string}`>()
  .refine((value) => {
    return value.startsWith("maac:");
  });

export type MaacRoleName = z.infer<typeof MaacRoleNameSchema>;

export const MaacPermissionSchema = z.enum([
  "maac:user_roles:unassign",
  "maac:user_roles:assign",
  "maac:contact:unmask",
  "maac:export_contact:unmask",
  "maac:admin_center:access",
]);

export type MaacPermission = z.infer<typeof MaacPermissionSchema>;

export const UserSchema = z.object({
  username: z.string().nullable(),
  email: z.string(),
  roles: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
});

const CategoryEnum = {
  TAG: 1,
  ENGAGEMENT_LEVEL: 2,
  JOIN_TIME: 3,
  LAST_ENGAGED: 4,
  GENDER: 5,
  PAGE_VIEW: 6,
  BROADCAST_CLICK: 7,
  TRACELINK_CLICK: 8,
  AVERAGE_TRANSACTION_REVENUE: 9,
  ACCUMULATED_REVENUE: 10,
  BIRTHDAY: 13,
  CUSTOMER_ID: 14,
  PHONE: 15,
  EMAIL: 16,
  CAMPAIGN_CLICK: 17,
  ANY_TAG: 19,
  CREATED_SOURCE: 20,
  OPEN_COUNT: 21,
  AUTO_SEGMENT: 22,
  TAG_INTENSITY: 23,
  CDP_TAG: 24,
  CUSTOM: 99 /** Default value */,
} as const;

export const CategorySchema = z.nativeEnum(CategoryEnum);

const TriggerTypeEnum = {
  DEFAULT: "default",
  BIND: "bind",
  POSTBACK: "postback",
  TAG: "tag",
  TAG_WITH_BOUND: "tag_with_bound",
} as const;

export const TriggerTypeSchema = z.nativeEnum(TriggerTypeEnum);

const JourneyNodeTriggerTypeEnum = {
  Empty: "empty",
  TagAdded: "tag_added",
  GaPageView: "ga_page_view",
  GaAddToCart: "ga_add_to_cart",
  GaPurchase: "ga_purchase",
  LineMessageOpened: "line_message_opened",
} as const;

export const JourneyNodeTriggerTypeSchema = z.nativeEnum(
  JourneyNodeTriggerTypeEnum,
);

const JourneyNodeRuleTypeEnum = {
  Exit: "exit",
  TimeDelay: "time_delay",
  TimeSchedule: "time_schedule",
  YesNoBranch: "yes_no_branch",
} as const;

export const JourneyNodeRuleTypeSchema = z.nativeEnum(JourneyNodeRuleTypeEnum);

const JourneyNodeActionTypeEnum = {
  SendLineMessage: "send_line_message",
} as const;

export const JourneyNodeActionTypeSchema = z.nativeEnum(
  JourneyNodeActionTypeEnum,
);

const JourneyNodeTypeEnum = {
  ...JourneyNodeTriggerTypeEnum,
  ...JourneyNodeRuleTypeEnum,
  ...JourneyNodeActionTypeEnum,
} as const;

export const JourneyNodeTypeSchema = z.nativeEnum(JourneyNodeTypeEnum);

export const MaacFeatureSettingsSchema = z
  .object({
    accessManagement: z
      .object({
        level: z.enum(["on", "off"]),
      })
      .nullable(),
    segmentSyncAdPlatform: z
      .object({
        level: z.enum(["on", "off"]),
      })
      .optional(),
    adIntegration: z.object({ level: z.enum(["on", "off"]) }).optional(),
  })
  .passthrough();

export const ProductFeedSourceTypeSchema = z.enum(["file_upload", "feed_url"]);

export const ProductFeedStatusSchema = z.enum([
  "success",
  "partial_success",
  "error",
  "syncing",
]);

export const ProcessingResultSchema = z.object({
  totalRows: z.number().int().nonnegative(),
  uploadedRows: z.number().int().nonnegative(),
  errors: z.record(z.string(), z.number().int().nonnegative()).optional(),
  warnings: z.record(z.string(), z.number().int().nonnegative()).optional(),
});

export const ProductFeedSchema = z.object({
  id: z.number().int().positive(),
  organizationId: z.number().int().positive(),
  name: z.string(),
  sourceType: ProductFeedSourceTypeSchema,
  status: ProductFeedStatusSchema,
  lastSyncAt: z.string().datetime().nullable(),
  feedUrl: z.string(),
  filename: z.string().nullable(),
  processingResult: ProcessingResultSchema.nullable(),
});

export const SignedUrlRequestSchema = z.object({
  filename: z.string(),
});

export const SignedUrlResponseSchema = z.object({
  signedUrl: z.string(),
  gcsUri: z.string(),
});

export const ProductFeedCreateRequestSchema = z.object({
  name: z.string(),
  feedUrl: z.string(),
  sourceType: ProductFeedSourceTypeSchema,
  filename: z.string().nullable().optional(),
});

export const ProductFeedUpdateRequestSchema = z.object({
  name: z.string(),
  feedUrl: z.string(),
  filename: z.string().nullable().optional(),
});

export const ProductFeedListResponseSchema = z.object({
  feeds: ProductFeedSchema.array(),
});

// Custom Field Schemas
export const CustomFieldTypeSchema = z.enum([
  "text",
  "float",
  "date",
  "timestamp",
  "single_select",
]);

/**
 * One choice on a single select field.
 *
 * `key` is the value contacts actually store. The server generates it once at
 * creation and never changes it, which is what lets `name` be renamed with no
 * data migration. It is never displayed and never sent back.
 *
 * There is no `position`: the array's order is the sort order.
 *
 * Archived options stay in the list with `active: false`. They are never
 * removed, so a contact holding an archived option's key can still be shown a
 * name rather than a machine value.
 */
export const CustomFieldOptionSchema = z.object({
  id: z.number().int().positive(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  active: z.boolean(),
});

/**
 * The type as it arrives from the API, which is not necessarily one this build
 * knows about.
 *
 * Interlude declares this field as a bare `str` and forwards whatever CDH
 * sends, so a type added on the backend reaches the frontend unfiltered and
 * before any release here. Parsed as a plain enum, one such row fails
 * `z.array(CustomFieldSchema)` and empties the entire list page; parsed this
 * way, it survives as `raw` and only that row's badge is affected.
 *
 * Requests keep using `CustomFieldTypeSchema`: the app only ever sends a type
 * it knows.
 */
export const SafeCustomFieldTypeSchema = safeEnumSchema(CustomFieldTypeSchema);

export const CustomFieldSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  key: z.string(),
  type: SafeCustomFieldTypeSchema,
  description: z.string(),
  slotNumber: z.number().int(),
  active: z.boolean(),
  /**
   * Only single select fields have options; the contract says every other
   * type returns the key with a `null` value.
   *
   * Optional as well as nullable, and it stays that way. The list endpoint
   * parses through `z.array(CustomFieldSchema)`, so one row the schema
   * rejects empties the whole page -- a backend that stops sending a key, or
   * has not started, must not be able to do that. Interlude does not send it
   * today: its response schema has no `options` field, and Django Ninja
   * serializes strictly from that schema, so the key is absent no matter what
   * CDH returns.
   *
   * This matches the rest of the file, where a key the backend may omit or
   * null is `.nullable().optional()`.
   */
  options: z.array(CustomFieldOptionSchema).nullable().optional(),
});

/**
 * An option in a create request. The server assigns `key` and defaults
 * `active` to true, and rejects a request that tries to set either.
 */
export const CreateCustomFieldOptionRequestSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
});

export const CreateCustomFieldRequestSchema = z.object({
  name: z.string(),
  key: z.string(),
  type: CustomFieldTypeSchema,
  description: z.string(),
  /**
   * Required for `single_select`, which needs at least one option, and
   * rejected for every other type. A field's type cannot be changed after
   * creation, so a single select field has to arrive with its options
   * already on it.
   *
   * Left as one optional key rather than split into a union discriminated on
   * `type`: the union would make this schema unusable from a form whose type
   * is only known at runtime without a narrowing branch in the create modal,
   * which is a change to that modal and not to this package. The server
   * rejects the wrong combination with
   * `CUSTOM_FIELD_SINGLE_SELECT_FIELD_TYPE_INVALID`.
   */
  options: z.array(CreateCustomFieldOptionRequestSchema).optional(),
});

export const UpdateCustomFieldRequestSchema = z.object({
  name: z.string(),
  description: z.string(),
});

/**
 * An option in a PATCH request, in its two forms: with an `id` it updates
 * that option, and without one it creates a new option.
 *
 * The split is a union rather than one shape with everything optional so that
 * an update cannot lose its `id` and a new option cannot lose its `name`
 * without the build noticing. An object matching neither form -- an empty one,
 * say -- is rejected here instead of at the server.
 *
 * Both members are `.strict()`, which is load-bearing rather than tidiness.
 * Zod strips unrecognized keys by default, so an update whose `id` has the
 * wrong type failed the first member and then passed the second with the bad
 * `id` quietly removed: a malformed update silently became a new option.
 * Strict mode makes the leftover `id` a rejection instead. It also catches
 * `key` and `status`, which the contract says never to send.
 */
export const PatchCustomFieldOptionRequestSchema = z.union([
  z
    .object({
      id: CustomFieldOptionSchema.shape.id,
      name: z.string().optional(),
      description: z.string().optional(),
      active: z.boolean().optional(),
    })
    .strict(),
  /**
   * A new option. `active` is accepted here and defaults to true, which is
   * the one place the PATCH and create shapes differ: the create request
   * takes only `name` and `description`.
   */
  z
    .object({
      name: z.string(),
      description: z.string().optional(),
      active: z.boolean().optional(),
    })
    .strict(),
]);

/**
 * Every key is optional and an absent one leaves that part of the field
 * alone. A request carrying none of them is rejected with
 * `CUSTOM_FIELD_PATCH_EMPTY`, so the caller has to drop a submit that changes
 * nothing rather than sending it.
 *
 * `options`, once sent, is the complete list. An existing option left out of
 * it is treated as a stale write and rejected with
 * `CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_STALE`; options are never hard-deleted,
 * and taking one out of use means sending it with `active: false`. An
 * unchanged option still has to appear, as `{ id }`.
 *
 * The array's order becomes the new sort order.
 */
export const PatchCustomFieldRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  options: z.array(PatchCustomFieldOptionRequestSchema).optional(),
});

// --- Billing V2 --- //

export const MigrationStatusResponseSchema = z.object({
  isNewBilling: z.boolean(),
});

export const FeatureLimitSchema = z.object({
  id: z.number().int().nullable(),
  product: BillingProductSchema,
  feature: BillingFeatureSchema,
  periodType: z.string(),
  limit: z.number(),
  warningThresholdPercent: z.number(),
  isActive: z.boolean(),
  usage: z.number(),
  usagePercent: z.number().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export const FeatureLimitsResponseSchema = z.object({
  featureLimits: z.array(FeatureLimitSchema),
});

export const BillingFeatureItemSchema = z.object({
  product: BillingProductSchema,
  feature: BillingFeatureSchema,
  unitAmount: z.string(),
  currency: z.string(),
});

export const BillingFeaturesResponseSchema = z.array(BillingFeatureItemSchema);

export type BillingFeatureItem = z.infer<typeof BillingFeatureItemSchema>;

export const UpdateFeatureLimitsResponseSchema = z.object({
  featureLimits: z.array(
    z.object({
      id: z.number().int(),
      organizationId: z.number().int(),
      product: BillingProductSchema,
      feature: BillingFeatureSchema,
      periodType: z.string(),
      limit: z.number(),
      warningThresholdPercent: z.number(),
      isActive: z.boolean(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
    }),
  ),
});

export const TopUpItemSchema = z.object({
  topupDisplayId: z.string(),
  status: TopUpStatusSchema,
  paymentMethod: PaymentMethodSchema.nullable(),
  poolId: z.number(),
  creditAmount: z.coerce.number(),
  taxAmount: z.coerce.number(),
  totalAmount: z.coerce.number(),
  companyName: z.string(),
  taxId: z.string().nullable(),
  memo: z.string().nullable(),
  currency: z.string(),
  createdAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
});

export const TopUpsResponseSchema = z.object({
  items: z.array(TopUpItemSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export const BillingAccessTokenResponseSchema = z.object({
  accessToken: z.string(),
});

export const NotifyEmailsResponseSchema = z.object({
  notifyEmails: z.array(z.string()),
});

export const UpdateNotifyEmailsResponseSchema = z.object({
  notifyEmails: z.array(z.string()),
});

export const BillingSettingsResponseSchema = z.object({
  monthlyReportEnabled: z.boolean(),
});

export const SettlementItemSchema = z.object({
  id: z.number().int(),
  product: BillingProductSchema.nullable(),
  feature: BillingFeatureSchema.nullable(),
  quantity: z.coerce.number(),
  unitPrice: z.coerce.number(),
  totalPrice: z.coerce.number(),
});

export const BalanceTransactionItemSchema = z.object({
  id: z.number().int(),
  poolId: z.number().int(),
  poolName: z.string(),
  amount: z.coerce.number(),
  balanceAfter: z.coerce.number(),
  currency: z.string(),
  sourceType: CreditSourceTypeSchema.nullable(),
  meterId: z.number().int().nullable(),
  description: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
  topupDisplayId: z.string().nullable(),
  settlementItem: SettlementItemSchema.nullable(),
});

export const BalanceTransactionsResponseSchema = z.object({
  items: z.array(BalanceTransactionItemSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type FeatureLimit = z.infer<typeof FeatureLimitSchema>;
export type TopUpItem = z.infer<typeof TopUpItemSchema>;

// ── Email Onboard ──

export const EmailOnboardDnsRecordSchema = z.object({
  type: z.string(),
  host: z.string(),
  value: z.string(),
  status: z.enum(["pending", "verified"]),
  reason: z.string().nullable().optional(),
});

export const EmailOnboardApplicationSchema = z.object({
  id: z.number().int().positive(),
  organizationId: z.number().int().positive(),
  brandDomain: z.string(),
  senderDomain: z.string(),
  dnsProvider: z.enum([
    "cloudflare",
    "godaddy",
    "google_cloud",
    "google_domains",
    "aws_route53",
    "other",
    "unknown",
  ]),
  senderProfilesData: z.array(
    z.object({
      name: z.string(),
      emailLocal: z.string(),
    }),
  ),
  replyToEmail: z.string(),
  status: z.enum(["draft", "pending_dns", "active", "failed"]),
  sendgridSubuserId: z.string().nullable().optional(),
  sendgridDomainId: z.string().nullable().optional(),
  dnsRecords: z.array(EmailOnboardDnsRecordSchema).default([]),
  dnsVerifiedAt: z.string().datetime().nullable().optional(),
  channelId: z.number().int().positive().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const EmailOnboardVerifyResponseSchema = z.object({
  overall: z.enum(["verified", "pending"]),
  perRecord: z.array(EmailOnboardDnsRecordSchema),
  status: z.enum(["draft", "pending_dns", "active", "failed"]),
});

export const EmailOnboardDraftSchema = z.object({
  organizationId: z.number().int().positive(),
  formData: z.record(z.unknown()),
  lastSavedAt: z.string().datetime(),
});

// Meta Ads Schemas
export const MetaAdsTokenStatusSchema = z.object({
  id: z.number(),
  orgUuid: z.string(),
  name: z.string(),
  tokenSource: z.string(),
  tokenStatus: z.string(),
  errorMessage: z.string().nullable().optional(),
});

export const MetaAdsConnectRequestSchema = z.object({
  code: z.string(),
});

export const MetaAdsAdAccountSchema = z.object({
  id: z.number(),
  externalAccountId: z.string(),
  name: z.string(),
});

export const MetaAdsDatasetSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const MetaAdsConnectResponseSchema = z.object({
  token: MetaAdsTokenStatusSchema,
  accounts: z.array(MetaAdsAdAccountSchema),
  datasets: z.array(MetaAdsDatasetSchema),
});

export const MetaAdsAccountListResponseSchema = z.object({
  accounts: z.array(MetaAdsAdAccountSchema),
  total: z.number(),
});

export const MetaAdsDatasetListResponseSchema = z.array(MetaAdsDatasetSchema);

export const LineAdsGroupSchema = z.object({
  id: z.number().int(),
  orgUuid: z.string().uuid(),
  externalGroupId: z.string(),
  name: z.string(),
  hasAccess: z.boolean().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export const RegisterLineAdsGroupRequestSchema = z.object({
  externalGroupId: z.string(),
  accessKey: z.string(),
  secretKey: z.string(),
});

export const LineAdsAccountSchema = z.object({
  id: z.number().int(),
  lineAdsGroupId: z.number().int(),
  externalAccountId: z.string(),
  name: z.string(),
  hasAccess: z.boolean().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const LineAdsAccountListResponseSchema = z.object({
  accounts: z.array(LineAdsAccountSchema),
  total: z.number().int(),
});

/**
 * Contract signing.
 */
export const ContractTypeSchema = z.enum(["msa", "addendum"]);

/** `all` belongs to the master service agreement, which covers every product. */
export const ContractProductSchema = z.enum(["maac", "caac", "daac", "all"]);

/**
 * Products a customer can sign for. Narrower than ContractProductSchema: a
 * signing session is always for one concrete product, never for `all`.
 */
export const SignableProductSchema = z.enum(["maac", "caac", "daac"]);

/**
 * OpenAPI v2 self-service API keys.
 */

export const ApiKeyStatusSchema = z.enum(["active", "disabled", "expired"]);

/**
 * The statuses a client may assign. Narrower than `ApiKeyStatusSchema`:
 * interlude derives `expired` by comparing the credential's expiry tag against
 * the current time, so it is reported but never written.
 */
export const SettableApiKeyStatusSchema = z.enum(["active", "disabled"]);

/**
 * The status as it arrives from the API, which is not necessarily one this
 * build knows about.
 *
 * `listApiKeys` parses through `z.array(ApiKeySchema)`, so a status added on
 * the backend before a release here would fail the array and empty the whole
 * key list. Parsed this way it survives as `raw` and only that row's status
 * cell is affected.
 *
 * Requests keep using `SettableApiKeyStatusSchema`: the app only ever sends a
 * status it knows.
 */
export const SafeApiKeyStatusSchema = safeEnumSchema(ApiKeyStatusSchema);

export const ApiKeySchema = z.object({
  /** The Kong credential id, which is what the write endpoints address. */
  id: z.string(),
  /**
   * The full plaintext key, returned by every endpoint in this api. Interlude
   * keeps the whole value on purpose and treats masking as a frontend
   * concern, so nothing is truncated here.
   */
  key: z.string(),
  name: z.string(),
  status: SafeApiKeyStatusSchema,
  /** Always UTC: interlude builds it from the Kong timestamp with `tz=utc`. */
  createdAt: z.string().datetime({ offset: true }),
});

/**
 * A Crescendo Lab MCP key, as the MCP connections page reads it.
 *
 * Interlude returns the same `ApiKeyOut` as for OpenAPI keys, plaintext `key`
 * included, but nothing here uses an MCP key's value: zod drops the
 * undeclared field, so the credential never reaches the query cache, and the
 * list keeps parsing if interlude stops sending it.
 */
export const McpApiKeySchema = ApiKeySchema.omit({ key: true });

export const CreateApiKeyRequestSchema = z.object({
  name: z.string(),
});

/**
 * Partial update. Interlude reads the body with `exclude_unset=True`, so an
 * omitted key leaves the field alone. Both fields are optional rather than
 * nullable: the backend rejects an explicit `null` name, and `undefined` is
 * dropped by `JSON.stringify` in interlude's request transform, so omission is
 * the only way to say "don't touch this".
 */
export const PatchApiKeyRequestSchema = z.object({
  name: z.string().optional(),
  status: SettableApiKeyStatusSchema.optional(),
});

/**
 * The org's OpenAPI v2 IP allowlist: the GET response and the PUT body alike.
 * An empty list means the allowlist is off. Interlude rejects an entry with
 * host bits set rather than clearing them, and stores each accepted entry in
 * Python's `ipaddress` spelling: a single host gains `/32` or `/128`, a
 * netmask becomes a prefix length, IPv6 is lowercased and compressed. So what
 * comes back can differ in spelling from what was sent, and repeats are kept.
 */
export const IpAllowlistSchema = z.object({
  ipAllowlist: z.array(z.string()),
});
